<template>
    <div
        v-if="showBanner"
        data-cookie-banner
        class="fixed bottom-0 left-0 right-0 z-[9999] w-full bg-[#017275] py-6 px-4 shadow-lg"
        style="position: fixed !important; bottom: 0 !important; left: 0 !important; right: 0 !important; z-index: 9999 !important;"
    >
        <div class="container mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
            <div class="text-white text-center md:text-left">
                <h3 class="text-lg font-semibold mb-1">Táto stránka používa cookies</h3>
                <p class="text-sm opacity-90">
                    Cookies používame na zlepšenie vášho zážitku a personalizáciu obsahu.
                </p>
            </div>
            <div class="flex flex-wrap justify-center gap-3 flex-shrink-0">
                <CDarkModeToggle />
                <button
                    @click="declineAll"
                    class="px-4 py-2 text-white border border-white rounded-lg hover:bg-white/10 transition focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white"
                >
                    Odmietnuť
                </button>
                <button
                    @click="acceptAll"
                    class="px-4 py-2 bg-white text-[#017275] font-semibold rounded-lg hover:bg-gray-100 transition focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white"
                >
                    Prijať všetko
                </button>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
const LEGACY_COOKIE_NAME = 'cookie_consent'
const COOKIE_ID_SEPARATOR = '~'
const NECESSARY_COOKIE_ID = 'ncc_n'
const ANALYTICS_COOKIE_ID = 'ga'
const MARKETING_COOKIE_ID = 'marketing'
// nuxt-cookie-control's own optional "functional" cookie (isIframeBlocked): embeds load only with it.
// Until Oct 2026 our necessary cookie had this id too, so declining still unblocked embeds.
const EMBEDS_COOKIE_ID = 'ncc_f'

const showBanner = ref(false)
const {
    cookiesEnabled,
    cookiesEnabledIds,
    isConsentGiven,
    isModalActive,
    moduleOptions
} = useCookieControl()

type ConsentCookieDefinition = {
    id: string
    targetCookieIds?: string[]
}

const dedupeCookiesById = (cookies: ConsentCookieDefinition[]) => {
    const uniqueCookies = new Map<string, ConsentCookieDefinition>()

    for (const cookie of cookies) {
        if (!uniqueCookies.has(cookie.id)) {
            uniqueCookies.set(cookie.id, cookie)
        }
    }

    return [...uniqueCookies.values()]
}

const expiresAt = new Date(Date.now() + moduleOptions.cookieExpiryOffsetMs)
const consentCookieOptions = {
    expires: expiresAt,
    ...moduleOptions.cookieOptions
}

const consentDecisionCookie = useCookie<string | undefined>(
    moduleOptions.cookieNameIsConsentGiven,
    consentCookieOptions
)
const enabledCookieIdsCookie = useCookie<string | undefined>(
    moduleOptions.cookieNameCookiesEnabledIds,
    consentCookieOptions
)
const legacyConsentCookie = useCookie<string | undefined>(
    LEGACY_COOKIE_NAME,
    consentCookieOptions
)

const configuredCookies = computed(() =>
    dedupeCookiesById([
        ...(moduleOptions.cookies.necessary as ConsentCookieDefinition[]),
        ...(moduleOptions.cookies.optional as ConsentCookieDefinition[])
    ])
)
const necessaryCookies = computed(() =>
    configuredCookies.value.filter((cookie) => cookie.id === NECESSARY_COOKIE_ID)
)
const optionalCookies = computed(() =>
    configuredCookies.value.filter((cookie) =>
        [ANALYTICS_COOKIE_ID, MARKETING_COOKIE_ID, EMBEDS_COOKIE_ID].includes(cookie.id)
    )
)
const allCookieIdsString = computed(() =>
    [...necessaryCookies.value, ...optionalCookies.value]
        .map((cookie) => cookie.id)
        .join('')
)
const optionalCookieIds = computed(() =>
    optionalCookies.value.map((cookie) => cookie.id)
)
const removeCookie = (name: string) => {
    useCookie<string | undefined>(name, moduleOptions.cookieOptions).value = undefined
}

const clearDisabledOptionalTargetCookies = (enabledOptionalIds: string[]) => {
    const disabledOptionalCookies = optionalCookies.value.filter(
        (cookie) => !enabledOptionalIds.includes(cookie.id)
    )

    for (const cookie of disabledOptionalCookies) {
        for (const targetCookieId of cookie.targetCookieIds || []) {
            removeCookie(targetCookieId)
        }
    }
}

const applyTrackingConsent = (enabledIds: string[]) => {
    const analyticsGranted = enabledIds.includes('ga')
    const marketingGranted = enabledIds.includes('marketing')
    const analyticsConsentState = analyticsGranted ? 'granted' : 'denied'
    const marketingConsentState = marketingGranted ? 'granted' : 'denied'

    window.dataLayer = window.dataLayer || []

    if (typeof window.gtag === 'function') {
        window.gtag('consent', 'update', {
            ad_storage: marketingConsentState,
            analytics_storage: analyticsConsentState,
            ad_personalization: marketingConsentState,
            ad_user_data: marketingConsentState
        })
    }

    window.dataLayer.push({
        event: 'consent_update',
        consent_analytics: analyticsConsentState,
        consent_marketing: marketingConsentState
    })

    if (typeof window.fbq === 'function') {
        if (marketingGranted) {
            window.fbq('consent', 'grant')
        } else {
            window.fbq('consent', 'revoke')
        }
    }
}

const persistConsent = (enabledOptionalIds: string[]) => {
    const enabledCookies = [
        ...necessaryCookies.value,
        ...optionalCookies.value.filter((cookie) =>
            enabledOptionalIds.includes(cookie.id)
        )
    ]
    const enabledIds = enabledCookies.map((cookie) => cookie.id)

    isConsentGiven.value = true
    cookiesEnabled.value = enabledCookies
    cookiesEnabledIds.value = enabledIds

    consentDecisionCookie.value = allCookieIdsString.value
    enabledCookieIdsCookie.value = enabledIds.join(COOKIE_ID_SEPARATOR)
    legacyConsentCookie.value = enabledOptionalIds.length > 0 ? 'all' : 'necessary'

    clearDisabledOptionalTargetCookies(enabledOptionalIds)
    applyTrackingConsent(enabledIds)
    showBanner.value = false
}

const acceptAll = () => {
    persistConsent(optionalCookieIds.value)
}

const declineAll = () => {
    persistConsent([])
}

// The "allow cookies" link in a blocked embed (CookieIframe) opens the module's modal, which we
// don't render. Bring this banner back instead.
watch(isModalActive, (isActive) => {
    if (!isActive) return
    showBanner.value = true
    isModalActive.value = false
})

onMounted(() => {
    window.grantCookieConsent = () => {
        acceptAll()
    }
    window.revokeCookieConsent = () => {
        declineAll()
    }

    if (isConsentGiven.value !== undefined) {
        const enabledIds = cookiesEnabledIds.value || []
        // Rewrite a consent stored before the necessary cookie got its own id (see EMBEDS_COOKIE_ID)
        if (!enabledIds.includes(NECESSARY_COOKIE_ID)) {
            const previousOptionalIds = enabledIds.filter((id) => [ANALYTICS_COOKIE_ID, MARKETING_COOKIE_ID].includes(id))
            // Only "Prijať všetko" (both) also allows embeds; keep any other choice exactly as it was
            persistConsent(previousOptionalIds.length === 2 ? optionalCookieIds.value : previousOptionalIds)
            return
        }

        applyTrackingConsent(enabledIds)
        showBanner.value = false
        return
    }

    const legacyConsent = legacyConsentCookie.value

    if (legacyConsent === 'all') {
        persistConsent(optionalCookieIds.value)
        return
    }

    if (legacyConsent === 'necessary') {
        persistConsent([])
        return
    }

    showBanner.value = true
})

// Type declarations for window
declare global {
    interface Window {
        dataLayer: any[]
        gtag?: (...args: any[]) => void
        fbq?: (...args: any[]) => void
        grantCookieConsent?: () => void
        revokeCookieConsent?: () => void
    }
}
</script>
